/**
 * Confirms a self-funding purchase after Stripe sends the consumer back. Stripe's return can beat the
 * webhook that records the payment, so while the session still shows no active access this reloads
 * it every two seconds, up to ten times, reporting `isConfirming`; after that it reports `hasTimedOut`
 * and `retry` starts again. Once access is active the caller moves on.
 */
import { useCallback, useEffect, useState } from 'react';
import { useDispatch, useSelector } from 'react-redux';
import { fetchSessionForAuthRequest, selectSession } from '@/model';
import { getSelfFundAccess } from '../lib/self-fund-access';

const CONFIRM_INTERVAL_MS = 2000;
const MAX_CONFIRM_ATTEMPTS = 10;

export function useConfirmSelfFundAccess(enabled: boolean) {
  const dispatch = useDispatch();
  const { clientStripe, isInvalidating } = useSelector(selectSession);
  const [attempts, setAttempts] = useState(0);
  const isActive = getSelfFundAccess(clientStripe) === 'active';
  const isWaiting = enabled && !isActive;
  const isConfirming = isWaiting && attempts < MAX_CONFIRM_ATTEMPTS;

  useEffect(() => {
    if (!isConfirming || isInvalidating) return;
    const timer = setTimeout(() => {
      setAttempts(count => count + 1);
      dispatch(fetchSessionForAuthRequest({ invalidating: true }));
    }, CONFIRM_INTERVAL_MS);
    return () => clearTimeout(timer);
  }, [isConfirming, isInvalidating, attempts, dispatch]);

  const retry = useCallback(() => setAttempts(0), []);

  return { isActive, isConfirming, hasTimedOut: isWaiting && !isConfirming, retry };
}
